import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * Lightweight client-side pagination footer for tables/lists on Dashboard.
 *
 * Usage:
 *   const [page, setPage] = useState(1);
 *   const [pageSize, setPageSize] = useState(50);
 *   const paged = items.slice((page-1)*pageSize, page*pageSize);
 *   ...render paged...
 *   <TablePagination total={items.length} page={page} pageSize={pageSize}
 *                    onPageChange={setPage} onPageSizeChange={setPageSize} />
 */
export default function TablePagination({
  total = 0,
  page = 1,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 200],
  label = "rows",
  testId = "table-pagination",
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIdx = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endIdx = Math.min(safePage * pageSize, total);

  if (total <= pageSize) return null;

  return (
    <div
      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-2 py-3 border-t border-gray-100 bg-gray-50/40"
      data-testid={testId}
    >
      <div className="text-sm text-gray-600" data-testid={`${testId}-status`}>
        Showing <span className="font-medium">{startIdx.toLocaleString("en-IN")}</span>
        {" – "}
        <span className="font-medium">{endIdx.toLocaleString("en-IN")}</span>
        {" of "}
        <span className="font-medium">{total.toLocaleString("en-IN")}</span>{" "}{label}
      </div>

      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Per page:</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => { onPageSizeChange(Number(v)); onPageChange?.(1); }}
            >
              <SelectTrigger className="h-8 w-[75px] text-xs" data-testid={`${testId}-page-size`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((s) => (
                  <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8"
            disabled={safePage <= 1}
            onClick={() => onPageChange(1)}
            data-testid={`${testId}-first`}
            aria-label="First page"
          >
            <ChevronsLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8"
            disabled={safePage <= 1}
            onClick={() => onPageChange(safePage - 1)}
            data-testid={`${testId}-prev`}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-sm text-gray-700 px-2" data-testid={`${testId}-indicator`}>
            Page <span className="font-semibold">{safePage}</span> of <span className="font-semibold">{totalPages}</span>
          </span>
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8"
            disabled={safePage >= totalPages}
            onClick={() => onPageChange(safePage + 1)}
            data-testid={`${testId}-next`}
            aria-label="Next page"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8"
            disabled={safePage >= totalPages}
            onClick={() => onPageChange(totalPages)}
            data-testid={`${testId}-last`}
            aria-label="Last page"
          >
            <ChevronsRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
